import { DomainHit, ProviderId } from './types';

/**
 * Squarified Treemap Layout Algorithm
 * Based on the classic algorithm by Bruls, Huizing, and van Wijk
 */

export interface TreeMapCell {
  domain: string;
  favicon: string;
  query?: string; // For query group cells
  isGroup?: boolean; // True if this is a query group
  children?: TreeMapCell[]; // Child cells for hierarchical layout
  
  // Size metrics
  totalCitations: number;
  area: number; // percentage of total area
  
  // Visual encoding
  dominance: number; // -1 (Claude) to 1 (OpenAI)
  dominanceCategory: 'claude-dominant' | 'balanced' | 'openai-dominant';
  quality: number; // 0 to 1 (based on used vs considered ratio)
  
  // Display data
  coverage: number;
  openaiCount: number;
  claudeCount: number;
  usedCount: number;
  consideredCount: number;
  
  // Layout (from squarified algorithm)
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LayoutItem {
  value: number;
  data: TreeMapCell;
}

/**
 * Calculate total citations with weighted "considered" count
 */
export function calculateTotalCitations(
  hit: DomainHit, 
  consideredWeight: number = 0.25
): number {
  const usedCount = hit.usedBy.size;
  const consideredCount = hit.consideredBy.size - usedCount; // don't double-count
  return usedCount + (consideredCount * consideredWeight);
}

/**
 * Calculate provider dominance from -1 (Claude) to 1 (OpenAI)
 */
export function calculateProviderDominance(hit: DomainHit): number {
  const openaiUsed = hit.providerBreakdown.openai?.usedCount || 0;
  const claudeUsed = hit.providerBreakdown.claude?.usedCount || 0;
  const total = openaiUsed + claudeUsed;
  
  if (total === 0) return 0;
  
  // Scale from -1 to 1
  return (openaiUsed - claudeUsed) / total;
}

/**
 * Calculate citation quality (ratio of used to total)
 */
export function calculateCitationQuality(hit: DomainHit): number {
  const used = hit.usedBy.size;
  const total = hit.usedBy.size + hit.consideredBy.size;
  
  if (total === 0) return 0;
  return used / total;
}

/**
 * Get category from dominance value
 */
export function getCategoryFromDominance(
  dominance: number
): 'claude-dominant' | 'balanced' | 'openai-dominant' {
  if (dominance < -0.3) return 'claude-dominant';
  if (dominance > 0.3) return 'openai-dominant';
  return 'balanced';
}

/**
 * Get color classes based on dominance category and quality
 */
export function getDominanceColor(
  category: 'claude-dominant' | 'balanced' | 'openai-dominant',
  quality: number
): { bg: string; border: string; text: string; opacity: number } {
  // Map quality (0-1) to opacity (0.3-0.8 for better visibility)
  const opacity = 0.3 + (quality * 0.5);
  
  const colors = {
    'claude-dominant': {
      bg: 'from-purple-600 to-purple-700',
      border: 'border-purple-400',
      text: 'text-white',
    },
    'balanced': {
      bg: 'from-indigo-600 to-violet-600',
      border: 'border-indigo-400',
      text: 'text-white',
    },
    'openai-dominant': {
      bg: 'from-blue-600 to-cyan-600',
      border: 'border-blue-400',
      text: 'text-white',
    },
  };
  
  return { ...colors[category], opacity };
}

/**
 * Calculate aspect ratio for a row
 */
function calculateAspectRatio(
  length: number,
  width: number,
  items: LayoutItem[]
): number {
  if (items.length === 0) return Infinity;
  
  const sum = items.reduce((acc, item) => acc + item.value, 0);
  const minValue = Math.min(...items.map(item => item.value));
  const maxValue = Math.max(...items.map(item => item.value));
  
  const area = sum / width;
  const minAspect = Math.max(
    (length * length * maxValue) / (sum * sum),
    (sum * sum) / (length * length * minValue)
  );
  
  return minAspect;
}

/**
 * Layout a single row of items
 */
function layoutRow(
  items: LayoutItem[],
  x: number,
  y: number,
  width: number,
  height: number,
  vertical: boolean
): TreeMapCell[] {
  const sum = items.reduce((acc, item) => acc + item.value, 0);
  let position = vertical ? y : x;
  
  return items.map(item => {
    const size = (item.value / sum) * (vertical ? height : width);
    
    const cell: TreeMapCell = {
      ...item.data,
      x: vertical ? x : position,
      y: vertical ? position : y,
      width: vertical ? width : size,
      height: vertical ? size : height,
    };
    
    position += size;
    return cell;
  });
}

/**
 * Squarified treemap algorithm
 */
function squarify(
  items: LayoutItem[],
  x: number,
  y: number,
  width: number,
  height: number
): TreeMapCell[] {
  if (items.length === 0) return [];
  if (items.length === 1) {
    return [{
      ...items[0].data,
      x,
      y,
      width,
      height,
    }];
  }
  
  const vertical = height > width;
  const length = vertical ? height : width;
  const breadth = vertical ? width : height;
  
  let currentRow: LayoutItem[] = [];
  let remainingItems = [...items];
  let result: TreeMapCell[] = [];
  let currentPosition = vertical ? y : x;
  
  while (remainingItems.length > 0) {
    const item = remainingItems[0];
    const testRow = [...currentRow, item];
    
    const currentAspect = currentRow.length > 0 
      ? calculateAspectRatio(breadth, length - (vertical ? (currentPosition - y) : (currentPosition - x)), currentRow)
      : Infinity;
    const testAspect = calculateAspectRatio(breadth, length - (vertical ? (currentPosition - y) : (currentPosition - x)), testRow);
    
    if (currentRow.length === 0 || testAspect <= currentAspect) {
      currentRow.push(item);
      remainingItems.shift();
    } else {
      // Layout current row
      const rowSum = currentRow.reduce((acc, r) => acc + r.value, 0);
      const totalSum = items.reduce((acc, i) => acc + i.value, 0);
      const rowBreadth = (rowSum / totalSum) * breadth;
      
      const rowCells = layoutRow(
        currentRow,
        vertical ? x : currentPosition,
        vertical ? currentPosition : y,
        vertical ? rowBreadth : length - (currentPosition - (vertical ? y : x)),
        vertical ? length - (currentPosition - y) : rowBreadth,
        vertical
      );
      
      result.push(...rowCells);
      currentPosition += rowBreadth;
      currentRow = [];
    }
  }
  
  // Layout any remaining items
  if (currentRow.length > 0) {
    const rowSum = currentRow.reduce((acc, r) => acc + r.value, 0);
    const totalSum = items.reduce((acc, i) => acc + i.value, 0);
    const rowBreadth = (rowSum / totalSum) * breadth;
    
    const rowCells = layoutRow(
      currentRow,
      vertical ? x : currentPosition,
      vertical ? currentPosition : y,
      vertical ? rowBreadth : length - (currentPosition - (vertical ? y : x)),
      vertical ? length - (currentPosition - y) : rowBreadth,
      vertical
    );
    
    result.push(...rowCells);
  }
  
  return result;
}

/**
 * Generate hierarchical treemap layout grouped by queries
 */
export function generateHierarchicalTreeMapLayout(
  domains: DomainHit[],
  queries: string[],
  totalQueries: number,
  consideredWeight: number,
  containerWidth: number = 1000,
  containerHeight: number = 600
): TreeMapCell[] {
  // Group domains by queries - usedBy contains actual query strings
  const queryGroups = new Map<string, DomainHit[]>();
  
  queries.forEach(query => {
    queryGroups.set(query, []);
  });
  
  // Assign domains to queries based on their usedBy set
  domains.forEach(hit => {
    hit.usedBy.forEach(query => {
      if (queryGroups.has(query)) {
        queryGroups.get(query)!.push(hit);
      }
    });
  });
  
  // Create group items based on total coverage in each query
  const groupItems: LayoutItem[] = [];
  
  queryGroups.forEach((domainsInQuery, query) => {
    if (domainsInQuery.length === 0) return;
    
    // Calculate total occurrences for THIS query (sum of all domain occurrences)
    const totalOccurrencesInQuery = domainsInQuery.length; // Each domain appears once per query
    
    // Calculate aggregate stats for the group
    const totalOpenAI = domainsInQuery.reduce((sum, hit) => sum + (hit.providerBreakdown.openai?.usedCount || 0), 0);
    const totalClaude = domainsInQuery.reduce((sum, hit) => sum + (hit.providerBreakdown.claude?.usedCount || 0), 0);
    const totalUsed = domainsInQuery.reduce((sum, hit) => sum + hit.usedBy.size, 0);
    const totalConsidered = domainsInQuery.reduce((sum, hit) => sum + hit.consideredBy.size, 0);
    
    const dominance = totalOpenAI + totalClaude > 0 
      ? (totalOpenAI - totalClaude) / (totalOpenAI + totalClaude) 
      : 0;
    
    groupItems.push({
      value: totalOccurrencesInQuery, // Size based on number of domains in this query
      data: {
        domain: query,
        query,
        isGroup: true,
        favicon: '',
        totalCitations: totalUsed,
        area: 0,
        dominance,
        dominanceCategory: getCategoryFromDominance(dominance),
        quality: totalConsidered > 0 ? totalUsed / totalConsidered : 0,
        coverage: totalOccurrencesInQuery, // Store as occurrence count
        openaiCount: totalOpenAI,
        claudeCount: totalClaude,
        usedCount: totalUsed,
        consideredCount: totalConsidered,
        children: domainsInQuery.map(hit => {
          // Each domain in this query gets percentage based on its occurrence within the query
          const occurrenceInQuery = 1; // Each domain appears once per query
          const percentageInQuery = (occurrenceInQuery / totalOccurrencesInQuery) * 100;
          
          const child: TreeMapCell = {
            domain: hit.domain,
            favicon: hit.favicon,
            totalCitations: hit.usedBy.size,
            area: 0,
            dominance: calculateProviderDominance(hit),
            dominanceCategory: getCategoryFromDominance(calculateProviderDominance(hit)),
            quality: calculateCitationQuality(hit),
            coverage: percentageInQuery, // Percentage within THIS query
            openaiCount: hit.providerBreakdown.openai?.usedCount || 0,
            claudeCount: hit.providerBreakdown.claude?.usedCount || 0,
            usedCount: hit.usedBy.size,
            consideredCount: hit.consideredBy.size,
            x: 0,
            y: 0,
            width: 0,
            height: 0,
          };
          return child;
        }),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
    });
  });
  
  // Sort groups by coverage (largest first)
  groupItems.sort((a, b) => b.value - a.value);
  
  // Normalize values
  const totalValue = groupItems.reduce((sum, item) => sum + item.value, 0);
  if (totalValue === 0) return [];
  
  groupItems.forEach(item => {
    item.data.area = (item.value / totalValue) * 100;
  });
  
  // Generate top-level layout for query groups
  const groupCells = squarify(groupItems, 0, 0, containerWidth, containerHeight);
  
  // For each group, layout its children
  const result: TreeMapCell[] = [];
  
  groupCells.forEach(groupCell => {
    if (!groupCell.children || groupCell.children.length === 0) {
      result.push(groupCell);
      return;
    }
    
    // Sort children by total citations (most occurrences = largest)
    const sortedChildren = [...groupCell.children].sort((a, b) => b.usedCount - a.usedCount);
    
    // Create layout items for children based on their occurrence count
    const childItems: LayoutItem[] = sortedChildren.map(child => ({
      value: child.usedCount, // Use total citations/occurrences as size
      data: {
        ...child,
        coverage: (child.usedCount / groupCell.usedCount) * 100, // Recalculate percentage within this query
      },
    }));
    
    // Layout children within the group's bounds (with padding for header and spacing)
    const headerHeight = 48; // Increased header height for better clickability
    const padding = 8; // Increased padding
    const childCells = squarify(
      childItems,
      groupCell.x + padding,
      groupCell.y + headerHeight + padding,
      groupCell.width - padding * 2,
      groupCell.height - headerHeight - padding * 2
    );
    
    // Add group header cell
    result.push(groupCell);
    
    // Add child cells
    result.push(...childCells);
  });
  
  return result;
}

/**
 * Generate treemap layout from domain data (flat, non-hierarchical)
 */
export function generateTreeMapLayout(
  domains: DomainHit[],
  totalQueries: number,
  consideredWeight: number,
  containerWidth: number = 1000,
  containerHeight: number = 600
): TreeMapCell[] {
  // Prepare data - use coverage percentage as size
  const items: LayoutItem[] = domains.map(hit => {
    const coverage = (hit.usedBy.size / totalQueries) * 100;
    const dominance = calculateProviderDominance(hit);
    const quality = calculateCitationQuality(hit);
    
    return {
      value: coverage, // Use coverage instead of citations
      data: {
        domain: hit.domain,
        favicon: hit.favicon,
        totalCitations: hit.usedBy.size,
        area: 0, // will be calculated
        dominance,
        dominanceCategory: getCategoryFromDominance(dominance),
        quality,
        coverage,
        openaiCount: hit.providerBreakdown.openai?.usedCount || 0,
        claudeCount: hit.providerBreakdown.claude?.usedCount || 0,
        usedCount: hit.usedBy.size,
        consideredCount: hit.consideredBy.size,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      },
    };
  });
  
  // Sort by value (largest first)
  items.sort((a, b) => b.value - a.value);
  
  // Normalize values
  const totalValue = items.reduce((sum, item) => sum + item.value, 0);
  if (totalValue === 0) return [];
  
  items.forEach(item => {
    item.data.area = (item.value / totalValue) * 100;
  });
  
  // Generate layout
  return squarify(items, 0, 0, containerWidth, containerHeight);
}
